import { Flags } from '@fullstory/client-api';
import { int64 } from '@fullstory/proto/google/protobuf';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../types';
import {
  IDs,
  int64IDs,
  makeUseAccess,
  makeUseCreateAccess,
  stringIDs,
} from '../../utils/accessControl';
import { useRequestState } from '../../utils/hooks';
import { filterOverlays } from '../overlays/utils';
import { MetricsRequestActions } from './actions';
import {
  getActiveSavedMetricsById,
  getAllMetricsData,
  getMetricsAccess,
  getSavedMetricsById,
} from './selectors';
import { MetricsById } from './types';

export const useFetchAllMetrics = (orgId: string) => {
  const dispatch = useDispatch<AppDispatch>();

  const fetch = useCallback(() => {
    dispatch(MetricsRequestActions.getAllMetrics(orgId));
  }, [dispatch, orgId]);

  const retry = useCallback(() => {
    dispatch(MetricsRequestActions.getAllMetrics(orgId));
  }, [dispatch, orgId]);

  useEffect(() => fetch(), [fetch]);
  return {
    fetch,
    retry,
  };
};

export const useAllSavedMetrics = (includeOverlayedObjects: boolean = false) => {
  const requestStateStatus = useRequestState(getAllMetricsData);
  const metricsById = useSelector(getSavedMetricsById);

  const filtered: MetricsById = useMemo(
    () => (includeOverlayedObjects ? metricsById : filterOverlays(metricsById)),
    [includeOverlayedObjects, metricsById],
  );

  return {
    ...requestStateStatus,
    byId: filtered,
  };
};

export const useActiveSavedMetrics = () => {
  const requestStateStatus = useRequestState(getAllMetricsData);
  const metricsById = useSelector(getActiveSavedMetricsById);
  const filtered = useMemo(() => filterOverlays(metricsById), [metricsById]);

  return {
    ...requestStateStatus,
    byId: filtered,
  };
};

const emptyUnnamed = {
  isLoading: false,
  isReady: true,
  hasError: false,
  unnamedMetric: undefined,
};
export const useFetchUnnamedMetric = (hash: string, orgId: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, lastRequested, data } = useSelector(
    (state: AppState) => state.metrics.unnamedMetrics,
  );
  const unnamedMetric = useMemo(() => data?.[hash], [data, hash]);
  useEffect(() => {
    if (hash && orgId && !unnamedMetric && !loading && !error) {
      dispatch(MetricsRequestActions.getUnnamedMetric({ hash, orgId })).catch(() =>
        console.error('Failed to get temporary metric state'),
      );
    }
  }, [dispatch, unnamedMetric, loading, hash, error, orgId]);
  if (!hash) {
    return emptyUnnamed;
  }
  return {
    isLoading: loading,
    isReady: !loading && !error && lastRequested > -1,
    hasError: error,
    unnamedMetric,
  };
};

const access = makeUseAccess(MetricsRequestActions.getAccess, getMetricsAccess);

export const useMetricsCreateAccess = makeUseCreateAccess(
  MetricsRequestActions.getAccess,
  getMetricsAccess,
);

export const useMetricsAccess = (ids: IDs, orgId: string, flags: Flags) => {
  const metricsById = useSelector(getSavedMetricsById);

  const filteredIds = useMemo(() => {
    const nonCanned = (id: string | int64) => !metricsById[id as string]?.canned;
    if (ids?.int64Ids) {
      return int64IDs(ids.int64Ids.ids.filter(nonCanned));
    }
    return stringIDs(ids?.stringIds?.ids.filter(nonCanned));
  }, [ids?.int64Ids, ids?.stringIds, metricsById]);

  return access(filteredIds, orgId, flags);
};
